import { Controller } from "@hotwired/stimulus"

// Add/remove rows on the invoice custom-fields settings form, and on any other form
// with the same shape (product packings reuse it).
//
// Removal drops the row from the DOM — jsonb-backed definitions are stored as a single
// array, so whatever rows are still present at submit time become the new list.
//
// Rows backed by ActiveRecord nested attributes are the exception: a persisted record
// that merely vanishes from the DOM is a record the server never hears about, and it
// survives the save. Those rows carry a _destroy field, and removal marks it instead.
export default class extends Controller {
  static targets = ["rows", "template", "addButton", "limitNotice"]
  static values = { max: Number }

  connect() {
    this.syncLimit()
  }

  add(event) {
    event.preventDefault()
    if (this.rowCount >= this.maxValue) return

    // A timestamp keeps each new row's param index unique against both the
    // server-rendered rows and any other row added in this session.
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, Date.now())
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()

    const row = wrapper.firstElementChild
    this.rowsTarget.appendChild(row)
    this.syncLimit()
    row.querySelector("input[type=text]")?.focus()

    // A fresh row is rendered from a blank record, so any label the surrounding form
    // derives from other fields is still at its default. Let that form fill it in.
    this.element.dispatchEvent(new CustomEvent("custom-fields:added", { bubbles: true }))
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-custom-fields-target='row']")
    if (!row) return

    const destroyField = row.querySelector("[data-destroy-field]")
    if (destroyField) {
      // Persisted nested-attributes row: tell the server to delete it, and hide it so
      // the field still posts. Never counted as present again — see rowCount.
      destroyField.value = "1"
      row.classList.add("hidden")
    } else {
      row.remove()
    }
    this.syncLimit()
  }

  get rowCount() {
    return [...this.rowsTarget.querySelectorAll("[data-custom-fields-target='row']")]
      .filter(row => row.querySelector("[data-destroy-field]")?.value !== "1").length
  }

  syncLimit() {
    const atLimit = this.rowCount >= this.maxValue
    this.addButtonTarget.classList.toggle("hidden", atLimit)
    this.limitNoticeTarget.classList.toggle("hidden", !atLimit)
  }
}
